import { useMemo, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiEdit3, FiPlus, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import type { NamedItem, Occurrence, OccurrenceDraft, User } from '../../types';
import { Button, Select, Spinner } from '../ui';
import { cn } from '../../lib/cn';
import { fmtDateShort, matchSearch, sla, slaBase } from '../../lib/occurrences';
import OccurrenceModal from './OccurrenceModal';

interface OccurrencesPageProps {
  occurrences: Occurrence[];
  cities: NamedItem[];
  occurrenceTypes: NamedItem[];
  users: User[];
  loading: boolean;
  onCreate: (draft: OccurrenceDraft) => Promise<void>;
  onUpdate: (id: string, draft: OccurrenceDraft) => Promise<void>;
  onConclude: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onReload: () => void;
}

const SLA_CLS: Record<string, string> = {
  ok: 'bg-emerald-100 text-emerald-700',
  warn: 'bg-amber-100 text-amber-700',
  crit: 'bg-red-100 text-red-700'
};

function SlaBadge({ o }: { o: Occurrence }) {
  const s = sla(slaBase(o));
  if (!s.label) return <span className="text-slate-400">—</span>;
  const icon = s.cls === 'ok' ? '🟢' : s.cls === 'warn' ? '🟡' : '🔴';
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold', SLA_CLS[s.cls])}>
      {icon} {s.label}
      {s.emergencia && ' · EMERGÊNCIA'}
    </span>
  );
}

export default function OccurrencesPage({
  occurrences,
  cities,
  occurrenceTypes,
  users,
  loading,
  onCreate,
  onUpdate,
  onConclude,
  onDelete,
  onReload
}: OccurrencesPageProps) {
  const [subTab, setSubTab] = useState<'pending' | 'done'>('pending');
  const [search, setSearch] = useState('');
  const [filterTecnico, setFilterTecnico] = useState('');
  const [filterCidade, setFilterCidade] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Occurrence | null>(null);

  const techOptions = useMemo(() => {
    const vals = new Set(occurrences.map((o) => o.tecnico).filter(Boolean));
    return Array.from(vals).sort();
  }, [occurrences]);

  const countPending = useMemo(() => occurrences.filter((o) => o.status === 'Pendente').length, [occurrences]);
  const countDone = useMemo(() => occurrences.filter((o) => o.status === 'Concluído').length, [occurrences]);

  const rows = useMemo(() => {
    const wanted = subTab === 'pending' ? 'Pendente' : 'Concluído';
    let list = occurrences.filter((o) => o.status === wanted);
    if (filterTecnico) list = list.filter((o) => o.tecnico === filterTecnico);
    if (filterCidade) list = list.filter((o) => o.cidade === filterCidade);
    if (filterTipo) list = list.filter((o) => o.ocorrencia === filterTipo);
    if (search.trim()) list = list.filter((o) => matchSearch(o, search.trim()));
    if (subTab === 'pending') {
      list = [...list].sort((a, b) => new Date(slaBase(a) || 0).getTime() - new Date(slaBase(b) || 0).getTime());
    } else {
      list = [...list].sort((a, b) => new Date(b.dataConclusao || 0).getTime() - new Date(a.dataConclusao || 0).getTime());
    }
    return list;
  }, [occurrences, subTab, filterTecnico, filterCidade, filterTipo, search]);

  const openNew = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (o: Occurrence) => {
    setEditing(o);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleSave = async (draft: OccurrenceDraft) => {
    if (editing) await onUpdate(editing._id, draft);
    else await onCreate(draft);
    closeModal();
  };

  const handleConclude = (o: Occurrence) => {
    if (!window.confirm(`Concluir a OS do contrato ${o.contrato}?`)) return;
    onConclude(o._id).catch((err) => window.alert((err as Error).message || 'Erro ao concluir.'));
  };
  const handleDelete = (o: Occurrence) => {
    const label = [o.contrato && `Contrato ${o.contrato}`, o.nomeCliente].filter(Boolean).join(' — ');
    if (!window.confirm(`Excluir permanentemente a OS${label ? ':\n' + label : ''}?\n\nEsta ação não pode ser desfeita.`)) return;
    onDelete(o._id).catch((err) => window.alert((err as Error).message || 'Erro ao excluir.'));
  };

  const th = 'px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap';
  const td = 'px-3 py-2.5 text-sm text-slate-700 whitespace-nowrap';

  const sitDot = (s: string) =>
    s === 'Conectado'
      ? 'bg-emerald-500'
      : s === 'Desconectado'
        ? 'bg-red-500'
        : 'bg-slate-300';

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1">
          <button
            onClick={() => setSubTab('pending')}
            className={cn(
              'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold transition',
              subTab === 'pending' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            )}
          >
            <FiAlertTriangle size={15} /> Em Andamento
            <span className={cn('rounded-full px-1.5 text-xs', subTab === 'pending' ? 'bg-white/25' : 'bg-slate-200')}>
              {countPending}
            </span>
          </button>
          <button
            onClick={() => setSubTab('done')}
            className={cn(
              'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold transition',
              subTab === 'done' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            )}
          >
            <FiCheckCircle size={15} /> Concluídas
            <span className={cn('rounded-full px-1.5 text-xs', subTab === 'done' ? 'bg-white/25' : 'bg-slate-200')}>
              {countDone}
            </span>
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onReload} title="Recarregar">
            <FiRefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Button size="sm" onClick={openNew}>
            <FiPlus size={16} /> Nova OS
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          className="h-9 min-w-[200px] flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          placeholder="Buscar por contrato, cliente, endereço, serial..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={filterTecnico} onChange={(e) => setFilterTecnico(e.target.value)} className="h-9 w-44">
          <option value="">Todos os técnicos</option>
          {techOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Select value={filterCidade} onChange={(e) => setFilterCidade(e.target.value)} className="h-9 w-40">
          <option value="">Todas as cidades</option>
          {cities.map((c) => (
            <option key={c.id} value={c.name}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)} className="h-9 w-48">
          <option value="">Todas as ocorrências</option>
          {occurrenceTypes.map((t) => (
            <option key={t.id} value={t.name}>
              {t.name}
            </option>
          ))}
        </Select>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{rows.length} OS</span>
      </div>

      {/* Tabela */}
      <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white">
        {rows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-10 text-slate-400">
            {loading ? (
              <>
                <Spinner className="h-6 w-6" /> Carregando ocorrências...
              </>
            ) : (
              <>
                <span className="text-4xl">{subTab === 'pending' ? '✅' : '📭'}</span>
                {subTab === 'pending' ? 'Nenhuma OS em andamento' : 'Nenhuma OS concluída'}
              </>
            )}
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr>
                <th className={th} title="Situação de conexão">Sit.</th>
                <th className={th}>Abertura / SLA</th>
                <th className={th}>Técnico</th>
                <th className={th}>Contrato</th>
                <th className={th}>Cliente</th>
                <th className={th}>Tempo</th>
                <th className={th}>Endereço</th>
                <th className={th}>Bairro</th>
                <th className={th}>Cidade</th>
                <th className={th}>B/P</th>
                <th className={th}>Serial</th>
                <th className={th}>Ocorrência</th>
                <th className={th}>Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((o) => (
                <tr key={o._id} className="hover:bg-slate-50">
                  <td className={cn(td, 'text-center')}>
                    <span className={cn('inline-block h-2.5 w-2.5 rounded-full', sitDot(o.situacao))} title={o.situacao || 'Não informada'} />
                  </td>
                  <td className={td}>
                    <div className="flex flex-col gap-1">
                      <span className="font-mono text-xs text-slate-500">{fmtDateShort(o.abertura)}</span>
                      {subTab === 'pending' ? (
                        <SlaBadge o={o} />
                      ) : (
                        <span className="text-xs text-emerald-600">✔ {fmtDateShort(o.dataConclusao)}</span>
                      )}
                    </div>
                  </td>
                  <td className={td}>
                    {o.tecnico ? <span className="font-semibold text-blue-700">{o.tecnico}</span> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className={cn(td, 'font-mono')}>{o.contrato || '—'}</td>
                  <td className={cn(td, 'max-w-[180px] truncate')} title={o.nomeCliente}>
                    {o.nomeCliente || '—'}
                  </td>
                  <td className={td}>
                    {o.tempoCliente === 'NOVO' ? (
                      <span className="rounded border border-cyan-300 bg-cyan-50 px-1.5 py-0.5 text-xs font-semibold text-cyan-700">NOVO</span>
                    ) : o.tempoCliente === 'ANTIGO' ? (
                      <span className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-xs font-semibold text-slate-600">ANTIGO</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={cn(td, 'max-w-[180px] truncate')} title={o.endereco}>
                    {o.endereco || '—'}
                  </td>
                  <td className={td}>{o.bairro || '—'}</td>
                  <td className={td}>{o.cidade || '—'}</td>
                  <td className={cn(td, 'font-mono')}>{o.board || o.port ? `${o.board || '—'}/${o.port || '—'}` : '—'}</td>
                  <td className={cn(td, 'font-mono')}>{o.serial || '—'}</td>
                  <td className={td}>
                    <span className="rounded bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">{o.ocorrencia}</span>
                  </td>
                  <td className={td}>
                    <div className="flex items-center gap-1">
                      {o.status === 'Pendente' && (
                        <button
                          onClick={() => handleConclude(o)}
                          title="Concluir"
                          className="rounded-md p-1.5 text-emerald-600 hover:bg-emerald-50"
                        >
                          <FiCheckCircle size={16} />
                        </button>
                      )}
                      <button onClick={() => openEdit(o)} title="Editar" className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100">
                        <FiEdit3 size={16} />
                      </button>
                      <button onClick={() => handleDelete(o)} title="Excluir" className="rounded-md p-1.5 text-red-600 hover:bg-red-50">
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <OccurrenceModal
          occurrence={editing}
          cities={cities}
          occurrenceTypes={occurrenceTypes}
          users={users}
          onClose={closeModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
